import React, { useEffect, useId, useRef, useState } from 'react';
import { Loader2, RotateCw } from 'lucide-react';

const HUBSPOT_PORTAL_ID = '49020777';
const HUBSPOT_FORM_SCRIPT_ID = 'nokodia-hubspot-forms-v2';
const HUBSPOT_FORM_SCRIPT_SRC = 'https://js.hsforms.net/forms/embed/v2.js';
const HUBSPOT_FORM_TOP_CROP = 28;
const HUBSPOT_FORM_FOOTER_CROP = 72;

type HubSpotFormsApi = {
  create: (options: {
    region: string;
    portalId: string;
    formId: string;
    formInstanceId?: string;
    target: string;
    onFormReady?: () => void;
  }) => void;
};

declare global {
  interface Window {
    hbspt?: { forms?: HubSpotFormsApi };
  }
}

let hubSpotScriptPromise: Promise<HubSpotFormsApi> | null = null;

function loadHubSpotFormsApi() {
  if (window.hbspt?.forms) return Promise.resolve(window.hbspt.forms);
  if (hubSpotScriptPromise) return hubSpotScriptPromise;

  hubSpotScriptPromise = new Promise<HubSpotFormsApi>((resolve, reject) => {
    const resolveApi = () => {
      if (window.hbspt?.forms) resolve(window.hbspt.forms);
      else reject(new Error('HubSpot Forms API unavailable'));
    };

    const existingScript = document.getElementById(HUBSPOT_FORM_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', resolveApi, { once: true });
      existingScript.addEventListener('error', () => reject(new Error('HubSpot Forms script failed')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.id = HUBSPOT_FORM_SCRIPT_ID;
    script.src = HUBSPOT_FORM_SCRIPT_SRC;
    script.async = true;
    script.addEventListener('load', resolveApi, { once: true });
    script.addEventListener('error', () => reject(new Error('HubSpot Forms script failed')), {
      once: true,
    });
    document.body.appendChild(script);
  }).catch((error) => {
    hubSpotScriptPromise = null;
    throw error;
  });

  return hubSpotScriptPromise;
}

interface HubSpotEmbeddedFormProps {
  formId: string;
  title: string;
  minHeight?: number;
}

const HubSpotEmbeddedForm: React.FC<HubSpotEmbeddedFormProps> = ({
  formId,
  title,
  minHeight = 760,
}) => {
  const reactId = useId();
  const targetId = `hubspot-form-${reactId.replace(/:/g, '')}`;
  const frameRef = useRef<HTMLDivElement>(null);
  const [renderKey, setRenderKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [visibleHeight, setVisibleHeight] = useState(minHeight);

  useEffect(() => {
    const container = frameRef.current;
    if (!container || typeof document === 'undefined') return;

    setIsLoading(true);
    setHasError(false);
    setVisibleHeight(minHeight);
    container.innerHTML = '';

    let cancelled = false;

    const prepareIframe = () => {
      const iframe = container.querySelector('iframe');
      if (!iframe) return;

      iframe.title = title;
      iframe.setAttribute('scrolling', 'no');

      const source = iframe.getAttribute('src');
      if (source?.startsWith('http://js.hsforms.net/')) {
        iframe.src = source.replace('http://', 'https://');
      }

      const measuredHeights = [
        Number.parseFloat(iframe.style.height),
        Number.parseFloat(container.style.height),
        iframe.getBoundingClientRect().height,
      ].filter(Number.isFinite);
      const embeddedHeight = measuredHeights.length ? Math.max(...measuredHeights) : 0;

      if (Number.isFinite(embeddedHeight) && embeddedHeight > HUBSPOT_FORM_FOOTER_CROP) {
        setVisibleHeight(
          Math.max(minHeight, embeddedHeight - HUBSPOT_FORM_TOP_CROP - HUBSPOT_FORM_FOOTER_CROP),
        );
      }

      setIsLoading(false);
      setHasError(false);
    };

    const observer = new MutationObserver(prepareIframe);
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'style'],
    });

    loadHubSpotFormsApi()
      .then((forms) => {
        if (cancelled) return;
        forms.create({
          region: 'na1',
          portalId: HUBSPOT_PORTAL_ID,
          formId,
          formInstanceId: targetId,
          target: `#${targetId}`,
          onFormReady: () => {
            if (cancelled) return;
            prepareIframe();
            window.requestAnimationFrame(() => {
              prepareIframe();
              container.closest<HTMLElement>('[role="dialog"]')?.scrollTo({ top: 0 });
            });
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          setIsLoading(false);
          setHasError(true);
        }
      });

    const timeout = window.setTimeout(() => {
      if (!container.querySelector('iframe')) {
        setIsLoading(false);
        setHasError(true);
      }
    }, 12000);

    return () => {
      cancelled = true;
      observer.disconnect();
      window.clearTimeout(timeout);
      container.innerHTML = '';
    };
  }, [formId, minHeight, renderKey, targetId, title]);

  return (
    <div
      className="hubspot-form-embed relative"
      style={
        {
          '--hubspot-form-min-height': `${minHeight}px`,
          height: `${visibleHeight}px`,
        } as React.CSSProperties
      }
    >
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
          <div className="flex flex-col items-center gap-3 text-center">
            <Loader2 aria-hidden="true" className="h-7 w-7 animate-spin text-[#D7398B]" />
            <p className="text-sm text-gray-600">Chargement du formulaire…</p>
          </div>
        </div>
      )}

      {hasError && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white px-6 text-center">
          <div className="max-w-sm">
            <p className="font-bold text-gray-900">Le formulaire n’a pas pu être chargé.</p>
            <button
              type="button"
              className="nokodia-btn-cta mt-5"
              onClick={() => setRenderKey((key) => key + 1)}
            >
              Réessayer
              <RotateCw aria-hidden="true" className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div
        ref={frameRef}
        id={targetId}
        className="hubspot-form-target -translate-y-7"
      />
    </div>
  );
};

export default HubSpotEmbeddedForm;
